import React from 'react';
import './About.css';

const About = () => {
  return (
    <div className="about-page">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="page-hero-content">
          <h1>About Us</h1>
          <p>Building healthy, safe, and thriving communities since 2015</p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card">
              <div className="mv-icon">
                <img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400" alt="Vision" />
              </div>
              <h2>Vision</h2>
              <p>
                To build inclusive, resilient, and empowered communities by catalyzing social transformation
                through grassroots interventions, knowledge sharing, and participatory action.
              </p>
            </div>

            <div className="mv-card">
              <div className="mv-icon">
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400" alt="Mission" />
              </div>
              <h2>Mission</h2>
              <p>
                The Bluepoint Org strives to create spaces and opportunities for individuals and communities,
                especially the vulnerable and marginalised, to grow, express, and act. We prioritise holistic
                empowerment, psychosocial development, gender equity, environmental sustainability, and
                rights-based approaches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="our-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Our Story</h2>
              <div className="section-line"></div>
              <p>
                <strong>The Bluepoint Org was established in 2015</strong> as a results-driven, not-for-profit enterprise.
                Our mission is to deliver impactful solutions to diverse clients, including National, State, and
                Local Governments, Public Sector entities, Development Agencies, Educational Institutions,
                Corporates, and Social Sector organizations.
              </p>
              <p>
                We specialize in offering coaching, consulting, research, campaign strategies, and program
                management services. With our Head Office based in Thiruvananthapuram, Kerala, India, we are
                committed to addressing critical societal challenges and creating measurable, sustainable change.
              </p>
            </div>
            <div className="story-image">
              <img src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=600" alt="Our Story" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Blue Point */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title">Why 'The Bluepoint Org'?</h2>
          <div className="section-line"></div>

          <div className="why-grid">
            <div className="why-content-text">
              <p>
                Inspired by immense and ancient knowledge, <strong>The Bluepoint Org</strong> represents the essence of
                life sustained by nature's elements: air, water, fire, land, and the sky. Among these, the sky,
                symbolized by the color blue, represents unbounded space, vastness, and infinite potential—just
                like the human mind.
              </p>

              <div className="blue-significance">
                <h3>The Significance of Blue</h3>
                <p>Blue is the color of depth and stability. It embodies:</p>
                <ul>
                  <li>Trust and Loyalty</li>
                  <li>Wisdom and Intelligence</li>
                  <li>Sincerity and Peace</li>
                  <li>Faith and Truth</li>
                </ul>
                <p>
                  As the color of the spirit, blue has a calming effect, invoking tranquility and fostering
                  balance and self-expression. From a psychological perspective, blue represents reliability
                  and responsibility, exhibiting inner security and confidence.
                </p>
              </div>

              <div className="philosophy">
                <h3>Our Philosophy</h3>
                <p>
                  At The Bluepoint Org, we believe every individual possesses vast potential and inner depth.
                  Our mission is to tap into these "blue points" of intellect and spirit, fostering growth and
                  transformation.
                </p>
                <div className="logo-meaning">
                  <p>
                    <span className="highlight-blue">The Bluepoint Org</span> symbolizes intellect and vast potential.<br/>
                    <span className="highlight-yellow">The yellow touch</span> signifies the brightness of education and enlightenment.
                  </p>
                  <p className="philosophy-tagline">
                    Together, they represent our determination to meet your needs with precision, passion, and purpose.
                  </p>
                </div>
              </div>
            </div>

            <div className="why-sidebar">
              <img src="https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=600" alt="Blue Sky" />
              <div className="quote-box">
                <p>"We can do it!"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="team-section">
        <div className="container">
          <h2 className="section-title">Our Team</h2>
          <div className="section-line"></div>

          <div className="team-content">
            <p className="team-intro">
              At The Blue Point Org, our team is composed of dedicated professionals with backgrounds in
              Social Work, Management, Psychology, and Education. Our collective expertise spans diverse
              fields including:
            </p>

            <div className="expertise-grid">
              <div className="expertise-item">Psychosocial Intervention Programs</div>
              <div className="expertise-item">Project Management</div>
              <div className="expertise-item">Training and Development</div>
              <div className="expertise-item">Research</div>
              <div className="expertise-item">HR and Organizational Development</div>
              <div className="expertise-item">Strategy Consulting</div>
              <div className="expertise-item">Community Development</div>
              <div className="expertise-item">Social and Behaviour Change Communication</div>
              <div className="expertise-item">Entrepreneurship Development</div>
              <div className="expertise-item">Monitoring and Evaluation</div>
            </div>

            <div className="team-highlight">
              <h3>Our Experience</h3>
              <p>
                With an average experience of over <strong>25 years</strong>, Team Blue Point has a proven track
                record in Management Consulting, Public Health, Education, Rural Development, Livelihoods,
                Agriculture, Environment, Sanitation, Tourism, and Micro, Small, and Medium Enterprises (MSMEs).
              </p>
              <p>
                Our team has successfully navigated through multiple government leaderships, policies, and
                economic cycles, always delivering impactful results. To enhance our capabilities, we collaborate
                with best-in-class partners to implement large-scale, high-impact programs.
              </p>
            </div>

            <div className="culture-box">
              <h3>Our Culture</h3>
              <p>
                The culture at The Blue Point Org emphasizes participation, constant innovation, and a
                results-oriented approach, enabling us to deliver strategic solutions that create measurable
                outcomes for our clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors We Cover */}
      <section className="sectors-section">
        <div className="container">
          <h2 className="section-title">Sectors We Cover</h2>
          <div className="section-line"></div>

          <div className="sectors-grid">
            <div className="sector-card">
              <div className="sector-icon">
                <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400" alt="Health" />
              </div>
              <h3>Health</h3>
              <p>Comprehensive health programs promoting physical and mental well-being</p>
            </div>

            <div className="sector-card">
              <div className="sector-icon">
                <img src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=400" alt="Education" />
              </div>
              <h3>Education</h3>
              <p>Transformative education initiatives for children and youth</p>
            </div>

            <div className="sector-card">
              <div className="sector-icon">
                <img src="https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400" alt="Child Protection" />
              </div>
              <h3>Child Protection</h3>
              <p>Safeguarding children and strengthening protection systems</p>
            </div>

            <div className="sector-card">
              <div className="sector-icon">
                <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400" alt="Sustainable Development" />
              </div>
              <h3>Sustainable Development</h3>
              <p>Tourism, Agriculture, Livelihood, Environment and Sanitation</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
